import * as React from 'react';
import { scrollToAnchor } from '@cian/newbuilding-utils';
import { useDispatch, useSelector } from 'react-redux';

import {
  trackGenplanControlsClick,
  trackGenplanEscrowLabelClick,
  trackGenplanFlatsOnFloorClick,
  trackHouseHover,
  trackHouseLinkClick,
  trackShowGenplan,
} from './tracking';
import { openAeroPhotoModal } from '../../actions/aeroPhotoTour';
import { setFloorPlanHouseId, setOpenFloorPlan } from '../../actions/floorPlan';
import { Genplan } from '../../components/Genplan';
import { House } from '../../components/Genplan/components/House';
import { HouseTooltip } from '../../components/Genplan/components/HouseTooltip';
import { InfrastructureItem } from '../../components/Genplan/components/InfrastructureItem';
import { GenplanControls } from '../../components/GenplanControls';
import { getIsAeroPhotoTourAvailable } from '../../selectors/aeroPhotoTour';
import { getGenplanSelector, getIsFloorPlanAvailable, getNewbuildingId } from '../../selectors/newbuilding';
import { EGenplanControlsType } from '../../types/analytics';
import { EGenplanInfrastructureCategory } from '../../types/newbuilding/genplan';

export function GenplanContainer() {
  const dispatch = useDispatch();

  const genplan = useSelector(getGenplanSelector);
  const newbuildingId = useSelector(getNewbuildingId);
  const isFloorPlanAvailable = useSelector(getIsFloorPlanAvailable);
  const isAeroPhotoTourAvailable = useSelector(getIsAeroPhotoTourAvailable);
  const [viewedHouses, setViewedHouses] = React.useState<number[]>([]);
  const [viewedInfrastructure, setViewedInfrastructure] = React.useState<EGenplanInfrastructureCategory[]>([]);

  const onHover = React.useCallback(
    (houseId: number) => {
      /* istanbul ignore else */
      if (!viewedHouses.includes(houseId)) {
        trackHouseHover(newbuildingId, houseId);
        setViewedHouses([...viewedHouses, houseId]);
      }
    },
    [newbuildingId, viewedHouses],
  );

  const onInfrastructurePinHover = React.useCallback(
    (category: EGenplanInfrastructureCategory) => {
      /* istanbul ignore else */
      if (!viewedInfrastructure.includes(category)) {
        setViewedInfrastructure([...viewedInfrastructure, category]);
      }
    },
    [newbuildingId, viewedInfrastructure],
  );

  const onHouseLinkClick = React.useCallback(
    (houseId: number, roomType: string) => {
      trackHouseLinkClick(newbuildingId, houseId, roomType);
    },
    [newbuildingId],
  );

  const handleAeroPhotoClick = React.useCallback(() => {
    dispatch(openAeroPhotoModal());
    trackGenplanControlsClick(newbuildingId, EGenplanControlsType.Panorama);
  }, [dispatch, newbuildingId]);

  if (!genplan) {
    return null;
  }

  /* istanbul ignore next */
  const onEscrowLabelClick = (houseId: number) => (event: React.MouseEvent<HTMLElement>) => {
    scrollToAnchor({
      anchor: '#escrow',
      offset: 100,
      hasRelocation: false,
      hasAnimation: true,
    })(event);
    trackGenplanEscrowLabelClick(newbuildingId, houseId);
  };

  const onFloorPlanClick = (inTooltip?: boolean, houseId?: number) => () => {
    dispatch(setFloorPlanHouseId(houseId || null));
    dispatch(setOpenFloorPlan(true));

    if (inTooltip) {
      trackGenplanFlatsOnFloorClick(newbuildingId, houseId);
    } else {
      trackGenplanControlsClick(newbuildingId, EGenplanControlsType.Floor);
    }
  };

  const { image, isHousesNamesEnabled, houses, genplanInfrastructure } = genplan;

  return (
    <Genplan
      image={image}
      onShow={trackShowGenplan}
      renderHouses={(containerWidth: number, containerHeight: number) =>
        houses.map(({ name, houseInfo, stats, isEscrow, isSalesStart, ...house }) => (
          <House
            key={house.id}
            {...house}
            name={name}
            isEscrow={isEscrow}
            isSalesStart={isSalesStart}
            emptyStats={!stats}
            showTitle={isHousesNamesEnabled}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            tooltip={
              <HouseTooltip
                name={name}
                houseId={house.id}
                houseInfo={houseInfo}
                stats={stats}
                isEscrow={isEscrow}
                isSalesStart={isSalesStart}
                onLinkClick={onHouseLinkClick}
                onEscrowLabelClick={onEscrowLabelClick(house.id)}
                isFloorPlanEnabled={isFloorPlanAvailable}
                onFloorPlanClick={onFloorPlanClick(true, house.id)}
              />
            }
            onHover={() => onHover(house.id)}
          />
        ))
      }
      renderInfrastructure={(containerWidth: number, containerHeight: number) => {
        return genplanInfrastructure.map(infrastructureItem => (
          <InfrastructureItem
            key={infrastructureItem.id}
            category={infrastructureItem.category}
            tooltipPoint={infrastructureItem.tooltipPoint}
            name={infrastructureItem.name}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            onHover={onInfrastructurePinHover}
          />
        ));
      }}
    >
      <GenplanControls
        isAeroPhotoTourAvailable={isAeroPhotoTourAvailable}
        isFloorPlanAvailable={isFloorPlanAvailable}
        onFloorPlanClick={onFloorPlanClick()}
        onAeroPhotoClick={handleAeroPhotoClick}
      />
    </Genplan>
  );
}

// eslint-disable-next-line import/no-default-export
export default GenplanContainer;
